import { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, Check, AlertTriangle, ExternalLink } from '@/utils/icons';
import { API_BASE_URL } from '@/utils/constants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { formatDateTime, formatTimeAgo } from '@/utils/formatters';
import {
  createApiKey,
  listApiKeys,
  revokeApiKey,
  listWebhooks,
  createWebhook,
  deleteWebhook,
  testWebhook,
} from '@/services/api';
import type { CreateApiKeyResult, InsightsApiKeyInfo, WebhookEndpointInfo } from '@/services/api';

/**
 * Endpoint adapter so the same manager UI serves both surfaces:
 * - Dev Console (default): /api/v1/developer/* — all keys, any org scope
 * - Settings: /api/integrations/* — keys scoped to the session's own org
 */
export interface ApiKeysManagerApi {
  createKey: (label: string, expiresAt?: string) => Promise<CreateApiKeyResult>;
  listKeys: () => Promise<{ keys: InsightsApiKeyInfo[] }>;
  revokeKey: (id: string) => Promise<{ success: boolean }>;
  listWebhooks: () => Promise<{ endpoints: WebhookEndpointInfo[] }>;
  createWebhook: (data: { apiKeyId: string; url: string; eventTypes: string[] }) => Promise<{ endpoint: WebhookEndpointInfo; secret: string }>;
  deleteWebhook: (id: string) => Promise<{ success: boolean }>;
  testWebhook: (id: string) => Promise<{ success: boolean; statusCode?: number; error?: string }>;
}

const developerApi: ApiKeysManagerApi = {
  createKey: createApiKey,
  listKeys: listApiKeys,
  revokeKey: revokeApiKey,
  listWebhooks,
  createWebhook,
  deleteWebhook,
  testWebhook,
};

interface ApiKeysManagerProps {
  api?: ApiKeysManagerApi;
  title?: string;
  description?: string;
}

export function ApiKeysManager({
  api = developerApi,
  title = 'External API',
  description = 'Manage API keys and webhooks for external integrations.',
}: ApiKeysManagerProps = {}) {
  const [keys, setKeys] = useState<InsightsApiKeyInfo[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookEndpointInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create key dialog
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [createdKeyPlaintext, setCreatedKeyPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Create webhook dialog
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookKeyId, setWebhookKeyId] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>([]);
  const [createdWebhookSecret, setCreatedWebhookSecret] = useState<string | null>(null);

  const EVENT_TYPES = [
    'node.state_changed',
    'node.created',
    'node.updated',
    'alert.created',
    'alert.resolved',
  ];

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [keysResult, webhooksResult] = await Promise.all([
        api.listKeys(),
        api.listWebhooks(),
      ]);
      setKeys(keysResult.keys);
      setWebhooks(webhooksResult.endpoints);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateKey() {
    try {
      const result = await api.createKey(
        newKeyLabel,
        newKeyExpiry || undefined
      );
      setCreatedKeyPlaintext(result.key.plaintext);
      setNewKeyLabel('');
      setNewKeyExpiry('');
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create key');
    }
  }

  async function handleRevokeKey(id: string) {
    try {
      await api.revokeKey(id);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    }
  }

  async function handleCopyKey() {
    if (createdKeyPlaintext) {
      await navigator.clipboard.writeText(createdKeyPlaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function handleCreateWebhook() {
    try {
      const result = await api.createWebhook({
        apiKeyId: webhookKeyId,
        url: webhookUrl,
        eventTypes: webhookEvents,
      });
      setCreatedWebhookSecret(result.secret);
      setWebhookUrl('');
      setWebhookKeyId('');
      setWebhookEvents([]);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create webhook');
    }
  }

  async function handleDeleteWebhook(id: string) {
    try {
      await api.deleteWebhook(id);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete webhook');
    }
  }

  async function handleTestWebhook(id: string) {
    try {
      const result = await api.testWebhook(id);
      if (result.success) {
        setError(null);
      } else {
        setError(`Webhook test failed: ${result.error || `HTTP ${result.statusCode}`}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test webhook');
    }
  }

  function toggleEvent(event: string) {
    setWebhookEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="dls-py-12 dls-text-center dls-text-muted-foreground">
          Loading API key management...
        </CardContent>
      </Card>
    );
  }

  const activeKeys = keys.filter((k) => !k.revokedAt);

  const docsUrl = API_BASE_URL ? `${API_BASE_URL}/docs` : '/docs';

  return (
    <div className="dls-space-y-6">
      {/* Section Header + API Docs Link */}
      <div className="dls-flex dls-items-center dls-justify-between">
        <div>
          <h2 className="dls-text-lg dls-font-semibold dls-tracking-tight">{title}</h2>
          <p className="dls-text-sm dls-text-muted-foreground">{description}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => window.open(docsUrl, '_blank')}>
          <ExternalLink className="dls-h-3.5 dls-w-3.5 dls-mr-1.5" />
          API Docs
        </Button>
      </div>

      {error && (
        <div className="dls-flex dls-items-center dls-gap-2 dls-p-3 dls-rounded-md dls-bg-status-offline/10 dls-text-status-offline dls-text-sm">
          <AlertTriangle className="dls-h-4 dls-w-4" />
          {error}
          <Button variant="ghost" size="sm" onClick={() => setError(null)} className="dls-ml-auto">Dismiss</Button>
        </div>
      )}

      {/* API Keys Section */}
      <Card>
        <CardHeader className="dls-flex dls-flex-row dls-items-center dls-justify-between dls-space-y-0">
          <CardTitle className="dls-text-base dls-flex dls-items-center dls-gap-2">
            <Key className="dls-h-4 dls-w-4" />
            API Keys
          </CardTitle>
          <Button size="sm" onClick={() => setShowCreateKey(true)}>
            <Plus className="dls-h-3.5 dls-w-3.5 dls-mr-1" />
            Create Key
          </Button>
        </CardHeader>
        <CardContent>
          {keys.length === 0 ? (
            <p className="dls-text-sm dls-text-muted-foreground dls-py-8 dls-text-center">
              No API keys created yet. Create one to enable external integrations.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Key Prefix</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="dls-w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="dls-font-medium">{key.label}</TableCell>
                    <TableCell>
                      <code className="dls-text-xs dls-bg-muted dls-px-1.5 dls-py-0.5 dls-rounded">{key.keyPrefix}...</code>
                    </TableCell>
                    <TableCell className="dls-text-sm">{key.createdByEmail}</TableCell>
                    <TableCell className="dls-text-sm">{formatTimeAgo(key.createdAt)}</TableCell>
                    <TableCell className="dls-text-sm">{key.lastUsedAt ? formatTimeAgo(key.lastUsedAt) : 'Never'}</TableCell>
                    <TableCell className="dls-text-sm">
                      {key.expiresAt ? formatDateTime(key.expiresAt) : 'Never'}
                    </TableCell>
                    <TableCell>
                      {key.revokedAt ? (
                        <Badge variant="secondary" className="dls-bg-status-offline/10 dls-text-status-offline">Revoked</Badge>
                      ) : key.expiresAt && new Date(key.expiresAt) < new Date() ? (
                        <Badge variant="secondary" className="dls-bg-status-warning/10 dls-text-status-warning">Expired</Badge>
                      ) : (
                        <Badge variant="secondary" className="dls-bg-status-online/10 dls-text-status-online">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {!key.revokedAt && (
                        <Button variant="ghost" size="sm" onClick={() => handleRevokeKey(key.id)} title="Revoke key">
                          <Trash2 className="dls-h-3.5 dls-w-3.5 dls-text-status-offline" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Webhooks Section */}
      <Card>
        <CardHeader className="dls-flex dls-flex-row dls-items-center dls-justify-between dls-space-y-0">
          <CardTitle className="dls-text-base dls-flex dls-items-center dls-gap-2">
            Webhooks
          </CardTitle>
          <Button
            size="sm"
            onClick={() => {
              setShowCreateWebhook(true);
              if (activeKeys.length > 0) setWebhookKeyId(activeKeys[0].id);
            }}
            disabled={activeKeys.length === 0}
          >
            <Plus className="dls-h-3.5 dls-w-3.5 dls-mr-1" />
            Register Webhook
          </Button>
        </CardHeader>
        <CardContent>
          {activeKeys.length === 0 ? (
            <p className="dls-text-sm dls-text-muted-foreground dls-py-8 dls-text-center">
              Create an API key first — webhooks are linked to API keys.
            </p>
          ) : webhooks.length === 0 ? (
            <p className="dls-text-sm dls-text-muted-foreground dls-py-8 dls-text-center">
              No webhooks registered. Register one to receive real-time event notifications.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>URL</TableHead>
                  <TableHead>Events</TableHead>
                  <TableHead>Last Delivery</TableHead>
                  <TableHead>Failures</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="dls-w-[120px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {webhooks.map((wh) => (
                  <TableRow key={wh.id}>
                    <TableCell className="dls-font-mono dls-text-xs dls-max-w-[300px] dls-truncate">{wh.url}</TableCell>
                    <TableCell>
                      <div className="dls-flex dls-flex-wrap dls-gap-1">
                        {wh.eventTypes.map((et) => (
                          <Badge key={et} variant="outline" className="dls-text-[10px]">{et}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="dls-text-sm">{wh.lastDeliveryAt ? formatTimeAgo(wh.lastDeliveryAt) : 'Never'}</TableCell>
                    <TableCell className="dls-text-sm">{wh.failureCount}</TableCell>
                    <TableCell>
                      {wh.isActive ? (
                        <Badge variant="secondary" className="dls-bg-status-online/10 dls-text-status-online">Active</Badge>
                      ) : (
                        <Badge variant="secondary" className="dls-bg-status-offline/10 dls-text-status-offline">Disabled</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="dls-flex dls-gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleTestWebhook(wh.id)} title="Send test event">
                          Test
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteWebhook(wh.id)} title="Delete webhook">
                          <Trash2 className="dls-h-3.5 dls-w-3.5 dls-text-status-offline" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={showCreateKey} onOpenChange={(open) => {
        setShowCreateKey(open);
        if (!open) { setCreatedKeyPlaintext(null); setCopied(false); }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createdKeyPlaintext ? 'API Key Created' : 'Create API Key'}</DialogTitle>
            <DialogDescription>
              {createdKeyPlaintext
                ? 'Copy your key now — it will not be shown again.'
                : 'Create a new API key for external integrations.'}
            </DialogDescription>
          </DialogHeader>

          {createdKeyPlaintext ? (
            <div className="dls-space-y-4">
              <div className="dls-flex dls-items-center dls-gap-2 dls-p-3 dls-rounded-md dls-bg-status-warning/10 dls-text-status-warning dls-text-sm">
                <AlertTriangle className="dls-h-4 dls-w-4 dls-flex-shrink-0" />
                This key will only be displayed once. Store it securely.
              </div>
              <div className="dls-flex dls-items-center dls-gap-2">
                <code className="dls-flex-1 dls-text-xs dls-bg-muted dls-p-3 dls-rounded dls-font-mono dls-break-all">{createdKeyPlaintext}</code>
                <Button variant="secondary" size="sm" onClick={handleCopyKey}>
                  {copied ? <Check className="dls-h-4 dls-w-4" /> : <Copy className="dls-h-4 dls-w-4" />}
                </Button>
              </div>
            </div>
          ) : (
            <div className="dls-space-y-4">
              <div>
                <label className="dls-text-sm dls-font-medium">Label</label>
                <Input
                  placeholder="e.g., Kyle's CRM integration"
                  value={newKeyLabel}
                  onChange={(e) => setNewKeyLabel(e.target.value)}
                  className="dls-mt-1"
                />
              </div>
              <div>
                <label className="dls-text-sm dls-font-medium">Expiry (optional)</label>
                <Input
                  type="datetime-local"
                  value={newKeyExpiry}
                  onChange={(e) => setNewKeyExpiry(e.target.value ? new Date(e.target.value).toISOString() : '')}
                  className="dls-mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {createdKeyPlaintext ? (
              <Button onClick={() => { setShowCreateKey(false); setCreatedKeyPlaintext(null); }}>Done</Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setShowCreateKey(false)}>Cancel</Button>
                <Button onClick={handleCreateKey} disabled={!newKeyLabel.trim()}>Create Key</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Webhook Dialog */}
      <Dialog open={showCreateWebhook} onOpenChange={(open) => {
        setShowCreateWebhook(open);
        if (!open) { setCreatedWebhookSecret(null); }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createdWebhookSecret ? 'Webhook Registered' : 'Register Webhook'}</DialogTitle>
            <DialogDescription>
              {createdWebhookSecret
                ? 'Copy your signing secret now — it will not be shown again.'
                : 'Register a URL to receive event notifications.'}
            </DialogDescription>
          </DialogHeader>

          {createdWebhookSecret ? (
            <div className="dls-space-y-4">
              <div className="dls-flex dls-items-center dls-gap-2 dls-p-3 dls-rounded-md dls-bg-status-warning/10 dls-text-status-warning dls-text-sm">
                <AlertTriangle className="dls-h-4 dls-w-4 dls-flex-shrink-0" />
                This HMAC signing secret will only be displayed once. Use it to verify webhook payloads.
              </div>
              <code className="dls-block dls-text-xs dls-bg-muted dls-p-3 dls-rounded dls-font-mono dls-break-all">{createdWebhookSecret}</code>
            </div>
          ) : (
            <div className="dls-space-y-4">
              <div>
                <label className="dls-text-sm dls-font-medium">API Key</label>
                <select
                  className="dls-mt-1 dls-w-full dls-rounded-md dls-border dls-border-border dls-px-3 dls-py-2 dls-text-sm"
                  value={webhookKeyId}
                  onChange={(e) => setWebhookKeyId(e.target.value)}
                >
                  {activeKeys.map((k) => (
                    <option key={k.id} value={k.id}>{k.label} ({k.keyPrefix}...)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="dls-text-sm dls-font-medium">Webhook URL</label>
                <Input
                  placeholder="https://your-tool.example.com/webhooks"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  className="dls-mt-1"
                />
              </div>
              <div>
                <label className="dls-text-sm dls-font-medium">Event Types</label>
                <div className="dls-flex dls-flex-wrap dls-gap-2 dls-mt-2">
                  {EVENT_TYPES.map((et) => (
                    <Badge
                      key={et}
                      variant={webhookEvents.includes(et) ? 'default' : 'outline'}
                      className="dls-cursor-pointer"
                      onClick={() => toggleEvent(et)}
                    >
                      {et}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {createdWebhookSecret ? (
              <Button onClick={() => { setShowCreateWebhook(false); setCreatedWebhookSecret(null); }}>Done</Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setShowCreateWebhook(false)}>Cancel</Button>
                <Button
                  onClick={handleCreateWebhook}
                  disabled={!webhookUrl.trim() || !webhookKeyId || webhookEvents.length === 0}
                >
                  Register
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
